import { computed, ref } from 'vue'
import api from '@/store/api/donor'
import {
  bandQualifiesForExtraRelief,
  findIncomeBandById,
  getClaimTaxYearConfig,
  V1_CLAIMABLE_PERIOD_KEY,
} from '@/helpers/claimTaxBack/taxYearConfig'
import {
  calculateClaimEstimateForBand,
  isHighRateEstimate,
} from '@/helpers/claimTaxBack/estimate'
import {
  CLAIM_FUNNEL_EVENTS,
  trackClaimFunnel,
} from '@/helpers/claimTaxBack/funnel'
import type {
  ClaimEstimate,
  ClaimTaxBackProfile,
  ClaimTaxPayMethod,
  ClaimTaxRegion,
} from '@/types/claimTaxBack'

/**
 * Claim tax back donor state (GET/PUT /me/claim-tax-back/profile). Mocked
 * until the real API ships; see CLAIM_TAX_BACK_HANDOVER.md. Module-level so the
 * sidebar badge, dashboard notification, checker and upgrade modal share one
 * profile.
 */

const profile = ref<ClaimTaxBackProfile | null>(null)
const isReady = ref(false)
const isLoading = ref(false)

let loadPromise: Promise<void> | null = null

/** The single tax year claimable in V1, with verified bands and thresholds. */
export const claimTaxYearConfig = getClaimTaxYearConfig(V1_CLAIMABLE_PERIOD_KEY)

async function loadProfile(): Promise<void> {
  if (isReady.value) return
  if (loadPromise) return loadPromise

  const run = async () => {
    isLoading.value = true
    try {
      const response = await api.fetchClaimTaxBackProfile()
      // 404 = feature not on the real API yet; leave the profile empty so the
      // dashboard renders without claim entry points rather than erroring.
      profile.value =
        response.status === 404 ? null : (response.data as ClaimTaxBackProfile)
      isReady.value = true
    } catch (error) {
      console.error('Failed to load claim tax back profile', error)
      // A failed GET must not leave the claim page on a spinner. The checker
      // and guide can render with an empty profile; the next visit retries.
      isReady.value = true
    } finally {
      isLoading.value = false
      loadPromise = null
    }
  }

  loadPromise = run()
  return loadPromise
}

async function saveProfile(
  update: Partial<ClaimTaxBackProfile>
): Promise<void> {
  const response = await api.putClaimTaxBackProfile(update)
  profile.value = response.data as ClaimTaxBackProfile
}

export function useClaimTaxBack() {
  if (!isReady.value && !isLoading.value && !loadPromise) {
    void loadProfile()
  }

  /** True once the checker has everything needed to reproduce an estimate. */
  const hasCompletedChecker = computed(
    () =>
      profile.value !== null &&
      profile.value.region !== null &&
      profile.value.incomeBandId !== null &&
      profile.value.manualGivingPence !== null
  )

  /**
   * The donor's saved estimate, rebuilt from their answers so the figure is
   * always reproducible from the versioned tax config (never stored).
   */
  const estimate = computed<ClaimEstimate | null>(() => {
    if (!hasCompletedChecker.value || !profile.value) return null
    return calculateClaimEstimateForBand(
      profile.value.manualGivingPence!,
      profile.value.incomeBandId!,
      claimTaxYearConfig
    )
  })

  /** "New" sidebar marker: the profile exists and the page has never been opened. */
  const showNewBadge = computed(
    () =>
      isReady.value && profile.value !== null && !profile.value.firstVisitedAt
  )

  /** Claim entry points only render when the (mock) API served a profile. */
  const isClaimAvailable = computed(
    () => isReady.value && profile.value !== null
  )

  const markVisited = async (): Promise<void> => {
    await loadProfile()
    if (!profile.value || profile.value.firstVisitedAt) return
    await saveProfile({ firstVisitedAt: new Date().toISOString() })
  }

  const saveCheckerResult = async (input: {
    region: ClaimTaxRegion
    incomeBandId: string
    manualGivingPence: number
  }): Promise<ClaimEstimate> => {
    const result = calculateClaimEstimateForBand(
      input.manualGivingPence,
      input.incomeBandId,
      claimTaxYearConfig
    )
    await saveProfile({
      region: input.region,
      incomeBandId: input.incomeBandId,
      manualGivingPence: input.manualGivingPence,
      // Requirement: mark the account when the checker shows above-basic tax.
      highRateTaxpayer: isHighRateEstimate(result),
    })
    return result
  }

  /** "Change my answers": clear the saved checker inputs and start over. */
  const resetChecker = async (): Promise<void> => {
    await saveProfile({
      region: null,
      incomeBandId: null,
      manualGivingPence: null,
      highRateTaxpayer: false,
    })
  }

  /**
   * Paid guide: "That's all my Gift Aided donations", or skip when the account
   * total is £0. Collapses the donations step so they can see how to claim.
   * Recording more donations later does not clear it; totals recalculate regardless.
   */
  const confirmDonations = async (): Promise<void> => {
    await saveProfile({ donationsConfirmedAt: new Date().toISOString() })
  }

  /** Donor says they have contacted HMRC for this year. Can be undone. */
  const setClaimed = async (claimed: boolean): Promise<void> => {
    await saveProfile({ claimed })
    trackClaimFunnel(
      claimed
        ? CLAIM_FUNNEL_EVENTS.YearClaimed
        : CLAIM_FUNNEL_EVENTS.YearUnclaimed,
      { period_key: claimTaxYearConfig.periodKey }
    )
  }

  /** Paid guide step 1: region, income band and, when they can claim extra, pay method. */
  const saveTaxStep = async (input: {
    region: ClaimTaxRegion
    incomeBandId: string
    payMethod: ClaimTaxPayMethod | null
  }): Promise<void> => {
    const band = findIncomeBandById(claimTaxYearConfig, input.incomeBandId)
    if (!band) {
      throw new Error(`Unknown income band "${input.incomeBandId}"`)
    }
    const canClaimExtra = bandQualifiesForExtraRelief(band, claimTaxYearConfig)
    await saveProfile({
      region: input.region,
      incomeBandId: input.incomeBandId,
      // Basic-rate donors have nothing extra to claim, so pay method is unused.
      payMethod: canClaimExtra ? input.payMethod : null,
      highRateTaxpayer: canClaimExtra,
      // A basic-rate answer cannot keep a claimed mark or donation labels.
      ...(canClaimExtra ? {} : { claimed: false }),
    })
  }

  return {
    profile,
    isReady,
    isLoading,
    isClaimAvailable,
    hasCompletedChecker,
    estimate,
    showNewBadge,
    markVisited,
    saveCheckerResult,
    resetChecker,
    saveTaxStep,
    confirmDonations,
    setClaimed,
    loadProfile,
  }
}
